import { useState, useCallback } from 'react'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Browser, Document, Trash2 } from 'blaze-ui/icons'
import { Plus } from 'blaze-ui/icons/20'
import { Pdf, Video } from 'blaze-ui/icons/24'
import { Voice } from 'blaze-ui/icons/16'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { AddWebpageButton } from 'screens/Campaign/CampaignContext/AddWebpageModal/AddWebpageButton'
import { UploadButton } from 'screens/Campaign/CampaignContext/UploadButton'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { AlmanacFile, Source, SourceMaterialsMediaObject, Website } from 'types'
import { CampaignSource, SourceRole } from './Types'

import Styles from './AddContextModal.module.scss'

interface AddContextModalProps {
  initialSources: CampaignSource[]
  onSave: (sources: CampaignSource[]) => void
}

const SourceIcon = ({ source }: { source: Source }) => {
  if (source.type === 'Website') {
    const website = source as Website
    if (website.favicon) {
      return <img alt="" src={website.favicon} className={Styles.sourceIconImg} />
    }
    return <Browser size={18} color="var(--dark-40)" />
  }

  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const mediaType = media.mime_type?.split('/')[0]
    const docType = media.mime_type?.split('/')[1] || media.mime_type?.split('.')[1]

    if (mediaType === 'image') {
      return <img alt="" src={media.source_url} className={Styles.sourceIconImg} />
    }
    if (mediaType === 'audio') return <Voice size={18} />
    if (mediaType === 'video') return <Video size={18} />
    if (docType === 'pdf') return <Pdf color="var(--red-50)" size={18} />
    return <Document size={18} />
  }

  return <Document size={18} />
}

const getSourceLabel = (source: Source): { name: string; type: string } => {
  if (source.type === 'Website') {
    const website = source as Website
    return { name: website.title || website.url, type: 'Website' }
  }
  if (source.type === 'SourceMaterialsMediaObject') {
    const media = source as SourceMaterialsMediaObject
    const mediaType = media.mime_type?.split('/')[0]
    const docType = media.mime_type?.split('/')[1] || media.mime_type?.split('.')[1]

    let typeLabel = 'Document'
    if (mediaType === 'audio') typeLabel = 'Audio'
    else if (mediaType === 'video') typeLabel = 'Video'
    else if (mediaType === 'image') typeLabel = 'Image'
    else if (docType === 'pdf') typeLabel = 'PDF file'

    const parts = media.source_url?.split('/') ?? []
    const name = parts[parts.length - 1] || media.source_url
    return { name, type: typeLabel }
  }
  return { name: 'Unknown', type: 'File' }
}

const canBeVisualAsset = (source: Source): boolean => {
  if (source.type !== 'SourceMaterialsMediaObject') return false
  const media = source as SourceMaterialsMediaObject
  const mediaType = media.mime_type?.split('/')[0]
  return mediaType === 'image' || mediaType === 'video'
}

const defaultRolesForSource = (source: Source): SourceRole[] => {
  if (canBeVisualAsset(source)) return ['source_material', 'visual_asset']
  return ['source_material']
}

export const AddContextModal = ({ initialSources, onSave }: AddContextModalProps) => {
  const { closeModal } = useModals()
  const [sources, setSources] = useState<CampaignSource[]>(initialSources)
  const [isUploading, setIsUploading] = useState(false)
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const uploadsProjectKey = uploadsProjectQuery.data?.project?.id ?? null

  const addSource = useCallback(
    async (source: Source) => {
      const roles = defaultRolesForSource(source)
      let mediaFileId: number | undefined

      if (canBeVisualAsset(source) && uploadsProjectKey) {
        const media = source as SourceMaterialsMediaObject
        const res = await MediaFileClient.create(workspaceId, {
          source_url: media.source_url,
          mime_type: media.mime_type,
          title: media.source_url.split('/').pop() ?? 'upload',
          project_key: uploadsProjectKey,
        })
        if (res.type === SUCCESS_RESPONSE_TYPE) {
          const { almanac_file } = res.data as { almanac_file: AlmanacFile }
          mediaFileId = almanac_file.media_file_id
        } else {
          showToastMessage('Could not register as visual asset', 'error', undefined, 5)
        }
      }

      setSources((prev) => [...prev, { source, roles, mediaFileId }])
    },
    [workspaceId, uploadsProjectKey],
  )

  const removeSource = useCallback((id: number | string) => {
    setSources((prev) => prev.filter((cs) => cs.source.id !== id))
  }, [])

  const toggleRole = useCallback((id: number | string, role: SourceRole) => {
    setSources((prev) =>
      prev.map((cs) => {
        if (cs.source.id !== id) return cs
        const hasRole = cs.roles.includes(role)
        if (hasRole && cs.roles.length === 1) return cs
        const roles = hasRole ? cs.roles.filter((r) => r !== role) : [...cs.roles, role]
        return { ...cs, roles }
      }),
    )
  }, [])

  const handleSave = useCallback(() => {
    onSave(sources)
    closeModal()
  }, [sources, onSave, closeModal])

  return (
    <Modal.Root size="sm" onPressOutside={closeModal} aria-label="Add Context">
      <Modal.Header title="Add Context" />
      <Modal.Content>
        <div className={Styles.body}>
          {sources.length === 0 ? (
            <div className={Styles.emptyState}>
              <div className={Styles.emptyIcon}>
                <Plus size={20} color="var(--dark-40)" />
              </div>
              <div className={Styles.emptyText}>
                <Text variant="secondary" color="var(--dark-60)">
                  No context added yet.
                </Text>
                <Text variant="secondary" color="var(--dark-60)">
                  Add a website or upload files to get started.
                </Text>
              </div>
              <div className={Styles.actions}>
                <AddWebpageButton isDisabled={isUploading} onSourceCreated={addSource} primaryAction="Add Website" />
                <UploadButton
                  variant="secondary"
                  acceptImages
                  useToastLoading
                  isDisabled={isUploading}
                  onSourceCreated={addSource}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                />
              </div>
            </div>
          ) : (
            <>
              <div className={Styles.sourceList}>
                {sources.map((cs) => {
                  const { name, type } = getSourceLabel(cs.source)
                  const visualAllowed = canBeVisualAsset(cs.source)
                  return (
                    <div key={cs.source.id} className={Styles.sourceCard}>
                      <div className={Styles.sourceCardTop}>
                        <div className={Styles.sourceIcon}>
                          <SourceIcon source={cs.source} />
                        </div>
                        <div className={Styles.sourceInfo}>
                          <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
                            {name}
                          </Text>
                          <Text variant="metadata" color="var(--dark-60)" lineClamp={1}>
                            {type}
                          </Text>
                        </div>
                        <IconButton
                          icon={Trash2}
                          variant="ghost"
                          size="sm"
                          title="Remove source"
                          isDisabled={isUploading}
                          onPress={() => removeSource(cs.source.id)}
                        />
                      </div>
                      <div className={Styles.roleToggle}>
                        <button
                          type="button"
                          className={`${Styles.rolePill} ${
                            cs.roles.includes('source_material') ? Styles.rolePillActive : ''
                          }`}
                          onClick={() => !isUploading && toggleRole(cs.source.id, 'source_material')}
                          disabled={isUploading}
                        >
                          Source material
                        </button>
                        <div
                          className={!visualAllowed ? Styles.rolePillTooltipWrapper : undefined}
                          data-tooltip={
                            !visualAllowed
                              ? `${getSourceLabel(cs.source).type}s can't be used as visual assets`
                              : undefined
                          }
                        >
                          <button
                            type="button"
                            className={`${Styles.rolePill} ${
                              cs.roles.includes('visual_asset') ? Styles.rolePillActive : ''
                            } ${!visualAllowed ? Styles.rolePillDisabled : ''}`}
                            onClick={() => visualAllowed && !isUploading && toggleRole(cs.source.id, 'visual_asset')}
                            disabled={!visualAllowed || isUploading}
                          >
                            Visual asset
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className={Styles.actions}>
                <AddWebpageButton isDisabled={isUploading} onSourceCreated={addSource} primaryAction="Add Website" />
                <UploadButton
                  variant="secondary"
                  acceptImages
                  useToastLoading
                  isDisabled={isUploading}
                  onSourceCreated={addSource}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                />
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" size="lg" isDisabled={isUploading} onPress={handleSave}>
            Save Context
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
